import { projectsProjectAccessDecisionSchema } from '@crest/module-projects/contracts/project-access-decision';
import { projectsProjectReferenceSnapshotSchema } from '@crest/module-projects/contracts/project-reference';
import { z } from 'zod';
export const estimatingXactimateNormalizationCapability = 'estimating.xactimate-normalization.v1';
export const estimatingXactimateSketchSpecificationsCapability = 'estimating.xactimate-sketch-specifications.v1';
export const estimatingXactimateExportJobsCapability = 'estimating.xactimate-export-jobs.v1';
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
const finiteNumber = z.number().finite();
const positiveQuantity = z.number().int().positive().max(10_000_000_000);
export const estimatingXactimateImportFormatSchema = z.enum(['auto', 'csv', 'paste']);
export const estimatingXactimateProjectTypeSchema = z.enum([
    'mitigation', 'mold', 'fire', 'carpet-cleaning', 'biohazard', 'reconstruction', 'other',
]);
export const estimatingXactimateNormalizeInputSchema = z.strictObject({
    format: estimatingXactimateImportFormatSchema.default('auto'),
    sourceText: z.string().min(1).max(5_000_000),
    sourceFileName: z.string().trim().min(1).max(255).nullable().default(null),
    sourceContentType: z.string().trim().min(1).max(160).nullable().default(null),
});
export const estimatingXactimateNormalizedLineSchema = z.strictObject({
    lineNumber: z.number().int().positive(),
    sourceCode: nullableText(160),
    name: z.string().trim().min(1).max(500),
    description: z.string().max(4_000),
    quantityTenThousandths: positiveQuantity,
    unit: z.string().trim().min(1).max(60),
    unitPriceCents: z.number().int().min(0).max(999_999_999_999),
    taxable: z.boolean(),
});
export const estimatingXactimateSourceFactsSchema = z.strictObject({
    customerName: nullableText(240),
    customerFirstName: nullableText(120),
    customerLastName: nullableText(120),
    customerEmail: nullableText(320),
    customerPhone: nullableText(80),
    propertyAddress: nullableText(500),
    propertyCity: nullableText(160),
    propertyRegion: nullableText(80),
    propertyPostalCode: nullableText(40),
    insuranceCompany: nullableText(240),
    claimNumber: nullableText(160),
    policyNumber: nullableText(160),
    adjusterName: nullableText(240),
    adjusterEmail: nullableText(320),
    adjusterPhone: nullableText(80),
    lossDate: z.string().date().nullable(),
    suggestedProjectType: estimatingXactimateProjectTypeSchema,
});
export const estimatingXactimateNormalizationResponseSchema = z.strictObject({
    data: z.strictObject({
        contractVersion: z.literal(estimatingXactimateNormalizationCapability),
        provider: z.literal('xactimate'),
        detectedFormat: z.enum(['csv', 'paste']),
        source: z.strictObject({
            fileName: nullableText(255),
            contentType: nullableText(160),
            byteLength: z.number().int().positive(),
            sha256: sha256Schema,
        }),
        lines: z.array(estimatingXactimateNormalizedLineSchema),
        sourceFacts: estimatingXactimateSourceFactsSchema,
        warnings: z.array(z.string().trim().min(1).max(500)),
    }),
});
export const estimatingXactimatePointSchema = z.strictObject({
    x: finiteNumber,
    y: finiteNumber,
    z: finiteNumber,
});
export const estimatingXactimateSketchSourceSchema = z.strictObject({
    project: z.strictObject({
        projectId: z.string().uuid(),
        projectNumber: nullableText(120),
        customerName: nullableText(240),
        address: z.strictObject({
            line1: nullableText(500),
            city: nullableText(160),
            region: nullableText(80),
            postalCode: nullableText(40),
        }),
    }),
    capture: z.strictObject({
        provider: z.enum(['crest-roomplan', 'plnar', 'manual', 'hybrid']),
        sourceCaptureId: nullableText(240),
        sourceRevision: z.number().int().nonnegative(),
        capturedAt: z.string().datetime({ offset: true }).nullable(),
        sourceUnits: z.enum(['feet', 'meters', 'inches']),
    }),
    levels: z.array(z.strictObject({
        levelId: z.string().trim().min(1).max(160),
        name: z.string().trim().min(1).max(240),
        elevationFeet: finiteNumber,
    })).min(1).max(100),
    rooms: z.array(z.strictObject({
        roomId: z.string().trim().min(1).max(240),
        levelId: z.string().trim().min(1).max(160),
        name: z.string().trim().min(1).max(240),
        roomType: nullableText(160),
        ceilingHeightFeet: z.number().finite().min(4).max(30),
        floorPolygon: z.array(estimatingXactimatePointSchema).min(3).max(1_000),
        quantities: z.strictObject({
            floorAreaSquareFeet: z.number().finite().nonnegative(),
            ceilingAreaSquareFeet: z.number().finite().nonnegative(),
            perimeterLinearFeet: z.number().finite().nonnegative(),
            wallAreaSquareFeet: z.number().finite().nonnegative(),
            volumeCubicFeet: z.number().finite().nonnegative(),
        }),
    })).min(1).max(1_000),
    openings: z.array(z.strictObject({
        openingId: z.string().trim().min(1).max(240),
        roomId: z.string().trim().min(1).max(240),
        wallId: z.string().trim().min(1).max(240).nullable().default(null),
        wallEdgeIndex: z.number().int().nonnegative().nullable().default(null),
        type: z.enum(['door', 'window', 'opening', 'missing-wall']),
        widthFeet: z.number().finite().positive(),
        heightFeet: z.number().finite().positive(),
        baseHeightFeet: z.number().finite().nonnegative(),
        offsetFromWallStartFeet: z.number().finite().nonnegative(),
        deductOpening: z.boolean(),
        sourceTag: nullableText(240),
    }).superRefine((value, context) => {
        if (value.wallId === null && value.wallEdgeIndex === null) {
            context.addIssue({ code: 'custom', path: ['wallId'], message: 'An opening needs a wall id or room edge index.' });
        }
    })).max(5_000).default([]),
    evidence: z.array(z.strictObject({
        evidenceId: z.string().trim().min(1).max(240),
        type: z.string().trim().min(1).max(120),
        artifactReference: z.record(z.string(), z.unknown()).nullable(),
        sha256: sha256Schema.nullable(),
    })).max(5_000).default([]),
});
const estimatingXactimateSpecSourceProjectSchema = z.strictObject({
    project_id: z.string().uuid(),
    job_number: nullableText(120),
    customer_name: nullableText(240),
    address: z.strictObject({
        line1: nullableText(500),
        city: nullableText(160),
        state: nullableText(80),
        zip: nullableText(40),
    }),
    captured_at: z.string().datetime({ offset: true }).nullable(),
    source_capture_id: nullableText(240),
    source_revision: z.number().int().nonnegative(),
});
export const estimatingXactimateSketchSpecSchema = z.strictObject({
    schema_version: z.literal('crest_xactimate_sketch_spec.v1'),
    provider: z.enum(['crest_roomplan', 'plnar', 'manual', 'hybrid']),
    source_project: estimatingXactimateSpecSourceProjectSchema,
    units: z.strictObject({
        source: z.enum(['feet', 'meters', 'inches']),
        export: z.literal('feet'),
        xactimate_sketch_units_per_foot: z.literal(1_524),
    }),
    levels: z.array(z.strictObject({
        level_id: z.string().trim().min(1).max(160),
        name: z.string().trim().min(1).max(240),
        floor_elevation: finiteNumber,
    })).min(1),
    vertices: z.array(z.strictObject({
        vertex_id: z.string().trim().min(1).max(240),
        level_id: z.string().trim().min(1).max(160),
        point: estimatingXactimatePointSchema,
        connected_wall_ids: z.array(z.string().trim().min(1).max(240)),
    })).min(1),
    walls: z.array(z.strictObject({
        wall_id: z.string().trim().min(1).max(240),
        level_id: z.string().trim().min(1).max(160),
        start_vertex_id: z.string().trim().min(1).max(240),
        end_vertex_id: z.string().trim().min(1).max(240),
        left_room_id: z.string().trim().min(1).max(240),
        right_room_id: z.string().trim().min(1).max(240).nullable(),
        height: z.number().finite().positive(),
        thickness: z.number().finite().positive(),
        is_exterior: z.boolean(),
        wall_type: z.string().trim().min(1).max(240),
    })).min(1),
    rooms: z.array(z.strictObject({
        room_id: z.string().trim().min(1).max(240),
        level_id: z.string().trim().min(1).max(160),
        name: z.string().trim().min(1).max(240),
        type: nullableText(160),
        ceiling_height: z.number().finite().positive(),
        wall_ids: z.array(z.string().trim().min(1).max(240)).min(3),
        floor_polygon: z.array(estimatingXactimatePointSchema).min(3),
        quantities: z.strictObject({
            F: z.number().finite().nonnegative(),
            C: z.number().finite().nonnegative(),
            PF: z.number().finite().nonnegative(),
            PC: z.number().finite().nonnegative().nullable(),
            W: z.number().finite().nonnegative(),
            V: z.number().finite().nonnegative(),
        }),
    })).min(1),
    openings: z.array(z.strictObject({
        opening_id: z.string().trim().min(1).max(240),
        wall_id: z.string().trim().min(1).max(240),
        room_id: z.string().trim().min(1).max(240).nullable(),
        type: z.enum(['door', 'window', 'opening', 'missing_wall']),
        width: z.number().finite().positive(),
        height: z.number().finite().positive(),
        base_height: z.number().finite().nonnegative(),
        offset_from_wall_start: z.number().finite().nonnegative(),
        deduct_opening: z.boolean(),
        source_tag: nullableText(240),
    })),
    objects: z.array(z.record(z.string(), z.unknown())),
    quantities: z.array(z.record(z.string(), z.unknown())),
    evidence: z.array(z.strictObject({
        evidence_id: z.string().trim().min(1).max(240),
        type: z.string().trim().min(1).max(120),
        artifact_reference: z.record(z.string(), z.unknown()).nullable(),
        sha256: sha256Schema.nullable(),
    })),
});
export const estimatingXactimateDiagnosticSchema = z.strictObject({
    code: z.string().trim().min(1).max(120),
    severity: z.enum(['error', 'warning']),
    path: z.string().trim().min(1).max(500),
    message: z.string().trim().min(1).max(1_000),
    roomId: z.string().trim().min(1).max(240).nullable().default(null),
    wallId: z.string().trim().min(1).max(240).nullable().default(null),
    openingId: z.string().trim().min(1).max(240).nullable().default(null),
});
export const estimatingXactimateSketchValidationSchema = z.strictObject({
    ok: z.boolean(),
    summary: z.strictObject({
        roomCount: z.number().int().nonnegative(),
        wallCount: z.number().int().nonnegative(),
        vertexCount: z.number().int().nonnegative(),
        openingCount: z.number().int().nonnegative(),
        evidenceCount: z.number().int().nonnegative(),
        floorAreaSquareFeet: z.number().finite().nonnegative(),
        ceilingAreaSquareFeet: z.number().finite().nonnegative(),
        wallAreaSquareFeet: z.number().finite().nonnegative(),
    }),
    errors: z.array(estimatingXactimateDiagnosticSchema),
    warnings: z.array(estimatingXactimateDiagnosticSchema),
});
export const estimatingXactimateSketchBuildResponseSchema = z.strictObject({
    data: z.strictObject({
        contractVersion: z.literal(estimatingXactimateSketchSpecificationsCapability),
        spec: estimatingXactimateSketchSpecSchema,
        validation: estimatingXactimateSketchValidationSchema,
    }),
});
export const estimatingXactimateExportJobStatusSchema = z.enum([
    'validation_failed', 'ready_for_compile', 'compiling', 'compiled',
    'compile_failed', 'import_verified', 'cancelled',
]);
export const estimatingXactimateAdapterKeySchema = z.enum([
    'xactimate-cloud', 'xactimate-windows',
]);
export const estimatingCreateXactimateExportJobCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: z.string().uuid(),
    jobId: z.string().uuid(),
    projectId: z.string().uuid(),
    sourceEstimateId: z.string().uuid().nullable().default(null),
    sourceEstimateVersionId: z.string().uuid().nullable().default(null),
    sourceEstimateRevision: z.number().int().positive().nullable().default(null),
    sourceFileName: z.string().trim().min(1).max(255).nullable().default(null),
    adapterKey: estimatingXactimateAdapterKeySchema,
    spec: estimatingXactimateSketchSpecSchema,
    reason: z.string().trim().min(10).max(500),
}).superRefine((value, context) => {
    const origin = [value.sourceEstimateId, value.sourceEstimateVersionId, value.sourceEstimateRevision];
    if (origin.some((entry) => entry !== null) && origin.some((entry) => entry === null)) {
        context.addIssue({ code: 'custom', path: ['sourceEstimateId'], message: 'Estimate origin must be complete.' });
    }
});
export const estimatingRetryXactimateExportJobCommandSchema = z.strictObject({
    kind: z.literal('retry'),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    adapterKey: estimatingXactimateAdapterKeySchema.optional(),
    reason: z.string().trim().min(10).max(500),
});
export const estimatingXactimateExportJobSchema = z.strictObject({
    contractVersion: z.literal(estimatingXactimateExportJobsCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    revision: z.number().int().positive(),
    projectReference: projectsProjectReferenceSnapshotSchema,
    projectAccessDecision: projectsProjectAccessDecisionSchema,
    sourceEstimateId: z.string().uuid().nullable(),
    sourceEstimateVersionId: z.string().uuid().nullable(),
    sourceEstimateRevision: z.number().int().positive().nullable(),
    sourceFileName: z.string().max(255).nullable(),
    adapterKey: estimatingXactimateAdapterKeySchema,
    specSchemaVersion: z.literal('crest_xactimate_sketch_spec.v1'),
    specHash: sha256Schema,
    spec: estimatingXactimateSketchSpecSchema,
    validation: estimatingXactimateSketchValidationSchema,
    status: estimatingXactimateExportJobStatusSchema,
    outputFileName: z.string().trim().min(5).max(255),
    internalEstimateName: z.string().trim().min(1).max(60),
    attemptCount: z.number().int().nonnegative(),
    maximumAttempts: z.number().int().min(1).max(10),
    availableAt: z.string().datetime({ offset: true }),
    lease: z.strictObject({
        workerId: z.string().trim().min(1).max(160),
        leaseToken: z.string().uuid(),
        claimedAt: z.string().datetime({ offset: true }),
        expiresAt: z.string().datetime({ offset: true }),
    }).nullable(),
    compiler: z.strictObject({
        version: z.string().trim().min(1).max(240),
        host: z.string().trim().min(1).max(240),
    }).nullable(),
    output: z.strictObject({
        artifactReference: z.record(z.string(), z.unknown()),
        sha256: sha256Schema,
        byteLength: z.number().int().positive(),
        fileName: z.string().trim().min(5).max(255),
        internalEstimateName: z.string().trim().min(1).max(60),
    }).nullable(),
    lastError: z.strictObject({
        code: z.string().trim().min(1).max(160),
        message: z.string().trim().min(1).max(2_000),
    }).nullable(),
    createdByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const estimatingXactimateExportJobResponseSchema = z.strictObject({
    data: estimatingXactimateExportJobSchema,
    command: z.strictObject({
        commandId: z.string().uuid(),
        idempotent: z.boolean(),
        deduplicated: z.boolean(),
    }).optional(),
});
export const estimatingXactimateExportJobsResponseSchema = z.strictObject({
    data: z.array(estimatingXactimateExportJobSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const estimatingXactimateExportJobListQuerySchema = z.strictObject({
    projectId: z.string().uuid().nullable().default(null),
    status: estimatingXactimateExportJobStatusSchema.nullable().default(null),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const estimatingXactimateWorkerClaimSchema = z.strictObject({
    jobId: z.string().uuid(),
    tenantId: z.string().uuid(),
    projectId: z.string().uuid(),
    attemptId: z.string().uuid(),
    attemptNumber: z.number().int().positive(),
    adapterKey: estimatingXactimateAdapterKeySchema,
    specSchemaVersion: z.literal('crest_xactimate_sketch_spec.v1'),
    specHash: sha256Schema,
    spec: estimatingXactimateSketchSpecSchema,
    outputFileName: z.string().trim().min(5).max(255),
    internalEstimateName: z.string().trim().min(1).max(60),
    workerId: z.string().trim().min(1).max(160),
    leaseToken: z.string().uuid(),
    leaseExpiresAt: z.string().datetime({ offset: true }),
});
export const estimatingXactimateWorkerResultSchema = z.strictObject({
    jobId: z.string().uuid(),
    leaseToken: z.string().uuid(),
    compilerVersion: z.string().trim().min(1).max(240),
    compilerHost: z.string().trim().min(1).max(240),
    output: z.strictObject({
        artifactReference: z.record(z.string(), z.unknown()),
        sha256: sha256Schema,
        byteLength: z.number().int().positive().max(1_000_000_000),
        fileName: z.string().trim().min(5).max(255),
        internalEstimateName: z.string().trim().min(1).max(60),
    }),
});
export const estimatingXactimateWorkerFailureSchema = z.strictObject({
    jobId: z.string().uuid(),
    leaseToken: z.string().uuid(),
    errorCode: z.string().regex(/^[a-z][a-z0-9_.-]{0,159}$/),
    errorMessage: z.string().trim().min(1).max(2_000),
    retryDelaySeconds: z.number().int().min(1).max(86_400),
});
//# sourceMappingURL=estimating-xactimate.contract.js.map